// Libraries
import styled, { css } from 'styled-components';

// Component
import { HighlightCardProps, HightlightCardStatus } from './HighlightCard.types';

// Types
import { Theme, ThemedProps } from '../../types/theme';

/*
 * This function maps the status to the color of the card.
 * It is used to ensure that the color of the card is consistent across the app.
 * It is used in the Status component.
 * @param {Theme} theme - The theme of the app.
 * @returns {Object} - An object with the status as the key and the color as the value.
*/
const mapStatusColor = (theme: Theme): Record<HightlightCardStatus, string> => ({
  [HightlightCardStatus.neutral]: theme.color.highlightCard.neutralStatusColor,
  [HightlightCardStatus.success]: theme.color.highlightCard.successStatusColor,
  [HightlightCardStatus.error]: theme.color.highlightCard.errorStatusColor,
});

export const Container = styled.div<{ status?: HightlightCardStatus } & ThemedProps>`
  display: flex;
  height: max-content;
  flex: 1;
  color: ${({
    status = HightlightCardStatus.neutral,
    theme,
  }) => mapStatusColor(theme)[status]};
`;

export const CardContent = styled.div<ThemedProps>`
  border: 1px solid ${({ theme }) => theme.color.highlightCard.borderColor};
  border-radius: 4px;
  width: 100%;
  height: 100%;
  padding-top: 16px;
  padding-right: 20px;
  padding-left: 20px;
  padding-bottom: 20px;
  flex-direction: column;
`;

export const TextContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  overflow: hidden;
`;

export const ContentContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Status = styled.div<{ status?: HightlightCardStatus } & ThemedProps>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({
    status = HightlightCardStatus.neutral,
    theme,
  }) => mapStatusColor(theme)[status]};
  align-items: center;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

export const ValueContainer = styled.div<{ unit: HighlightCardProps['unit'] }>`
  display: flex;
  align-items: center;
  /* Apply gap only if the unit is not % */
  ${({ unit }) => (
    unit !== '%'
      ? css`gap: 4px;`
      : css`gap: inherit;`
  )}
`;

export const UnitContainer = styled.div`
  padding-top: 2px;
`;
